/**
 * plugin 市集（批153 批A）——對應後端 `app/api/admin_plugin_market.py`（`/api/admin/plugin-market/*`）。
 * 規格：`license-server/docs/PLUGIN_MARKET.md`。
 *
 * 權限：讀與日常操作（登記、上傳、撤回、改呈現欄位、權利、立即同步）是 admin 以上；改 plugin 的
 * `status`、發行者、簽章公鑰、上架與下架要 manager 以上（`lib/pluginMarket.ts::canManageMarket`）。
 * 登錄簽章公鑰是**網頁專屬**（API 金鑰 403）——本 SPA 是網頁登入，照常可用。
 *
 * 批B（`temp/batch153/CONTRACT_B.md` §4.1）：開通第三方發行者、上架申請的婉拒、審核佇列與版本的
 * 核准／婉拒（manager）、待審包下載與指派碼（admin）。
 */
import { filenameFromDisposition } from '@/lib/download'

import { api, apiUpload, qs } from './client'

export type Visibility = 'private' | 'public'
export type PluginStatus = 'active' | 'suspended'
export type VersionStatus = 'uploading' | 'ready' | 'failed' | 'withdrawn'
export type DlAccess = 'private' | 'public' | null
/** 批B：第三方公開上架申請的狀態。 */
export type ListingStatus = 'none' | 'pending' | 'approved' | 'rejected'
/** 批B：第三方公開 plugin 每一版的審核狀態（null＝不必審：私人、第一方、或批B 之前的版本）。 */
export type ReviewStatus = 'pending' | 'approved' | 'rejected'

export interface Publisher {
  id: string
  kind: 'first_party' | 'third_party' | string
  first_party: boolean
  customer_id: string | null
  customer_name: string | null
  key_prefix: string | null
  display_name: string
  status: PluginStatus | string
  notes: string | null
  created_by: string | null
  created_at: string | null
  updated_at: string | null
  /** 清單才有：有效公鑰數與名下 plugin 數。 */
  active_keys?: number
  plugins?: number
}

export interface PublisherKey {
  id: string
  /** raw 32 bytes 的標準 base64。 */
  public_key: string
  /** sha256(raw 公鑰) 小寫 hex——信任一律比它。 */
  fingerprint: string
  label: string | null
  status: 'active' | 'revoked' | string
  created_by: string | null
  created_at: string | null
  revoked_by: string | null
  revoked_at: string | null
  revoke_reason: string | null
}

export interface PublisherDetail extends Publisher {
  keys: PublisherKey[]
  plugins: number
}

export interface DocLink {
  title: string
  url: string | null
}

export interface MarketPlugin {
  id: string
  publisher_id: string
  publisher_name: string | null
  first_party: boolean | null
  key: string
  product: string
  name: string
  summary: string
  description_md: string
  icon_data_uri: string | null
  homepage_url: string | null
  docs: DocLink[]
  sort_order: number
  status: PluginStatus | string
  visibility: Visibility | string
  /** 第一次上架的時間（下架也保留；null＝從沒公開過，它的撤回不進公開 feed）。 */
  first_published_at: string | null
  /**
   * 第三方申請公開上架的審核狀態（批B）：`none`／`pending`／`approved`／`rejected`。
   * 第一方恆為 `none`（第一方不審，manager 直接上架）。
   */
  listing_status: ListingStatus | string
  /** 批B：申請與審核的記錄（契約 §1 的投影名；舊回應缺席）。 */
  listing_requested_at?: string | null
  listing_requested_by?: string | null
  listing_reviewed_at?: string | null
  listing_reviewed_by?: string | null
  /** 婉拒理由（`listing_status='rejected'` 時有值）。 */
  listing_note?: string | null
  /** dl 上的市集 channel（LS 隨機產生、不從 key 推導）。 */
  dl_channel: string
  /** dl 最後一次確認的存取（null＝還沒建 channel＝從沒上傳過）。 */
  dl_access: DlAccess | string
  dl_synced_at: string | null
  dl_error: string | null
  latest_version: string | null
  created_by: string | null
  created_at: string | null
  updated_at: string | null
}

export interface MarketVersion {
  id: string
  plugin_id: string
  version: string
  status: VersionStatus | string
  /**
   * 審核狀態（批B）：第三方**公開** plugin 上傳的版本是 `pending`（`status` 仍是 `ready`，但不進
   * feed、清單看不到）→ manager 核准成 `approved` 或婉拒成 `rejected`（`status` 同時變 `failed`）。
   */
  review_status: ReviewStatus | string | null
  /** 婉拒理由。 */
  review_note?: string | null
  reviewed_at?: string | null
  reviewed_by?: string | null
  /** IPOPKG1 token 第 4 段（payload.tar.gz）——CS 的撤回清單以它為鍵。 */
  payload_sha256: string
  /** 整個 `.ipoplug` 的 sha256。 */
  sha256: string
  size: number
  signer_key_id: string
  signer_fingerprint: string
  min_cs_version: string | null
  sdk_version: number | null
  requested_scopes: string[] | null
  capabilities: Record<string, boolean>
  release_notes: string | null
  uploaded_by: string | null
  uploaded_realm: string
  created_at: string | null
  ready_at: string | null
  dl_error: string | null
  withdrawn_at: string | null
  withdrawn_by: string | null
  withdraw_reason: string | null
  dl_deleted_at: string | null
}

export interface PluginDetail extends MarketPlugin {
  versions: MarketVersion[]
  active_grants: number
  /** dl 上這條 channel **該是**什麼存取（與 `dl_access` 不同＝漂移、背景會收斂）。 */
  desired_dl_access: 'private' | 'public' | null
}

export interface PluginGrant {
  id: string
  plugin_id: string
  plugin_key: string | null
  plugin_name: string | null
  customer_id: string
  customer_name: string | null
  source: 'staff' | 'grant_code' | string
  status: 'active' | 'revoked' | string
  /** active 且未到期（到期不改 status，看這一欄）。 */
  effective: boolean
  expired: boolean
  expires_at: string | null
  note: string | null
  granted_by: string | null
  granted_realm: string
  created_at: string | null
  updated_at: string | null
  revoked_at: string | null
  revoked_by: string | null
  revoke_reason: string | null
}

export interface Page<T> {
  items: T[]
  /** 不透明游標；null＝沒有下一頁。 */
  next_cursor: string | null
}

export interface SyncStatus {
  ns: string | null
  enabled: boolean
  market_kid: string | null
  key_available: boolean
  audience: string | null
  feed: {
    sha256: string | null
    etag: string | null
    pushed_at: string | null
    last_attempt_at: string | null
    last_error: string | null
  }
  pending: {
    stuck_uploads: number
    withdraw_deletes: number
    access_drift: number
    /** 已按過核准、發布主機那一半還沒完成的版本數（舊後端缺席）。 */
    approvals?: number
  }
}

export interface PluginIn {
  publisher_id?: string | null
  key: string
  product: string
  name: string
  summary?: string
  description_md?: string
  icon_data_uri?: string | null
  homepage_url?: string | null
  docs?: DocLink[]
  sort_order?: number
}

/** 批B #2：開通第三方發行者（manager）。前綴格式 `^[a-z][a-z0-9]{2,13}_$`。 */
export interface PublisherIn {
  customer_id: string
  key_prefix: string
  display_name: string
  notes?: string | null
}

/**
 * 批B：指派碼（後台投影，**含兌換的客戶**——發行者在 portal 看到的是匿名化版本）。
 * 碼只存 sha256；`code_hint` 是末四碼。明碼只在建立的回應（`IssuedGrantCode.code`）出現一次。
 */
export type GrantCodeStatus = 'issued' | 'redeemed' | 'void'
export interface GrantCode {
  id: string
  plugin_id: string
  /** 末四碼（明碼 16 碼 body 的最後四個字元）。 */
  code_hint: string | null
  status: GrantCodeStatus | string
  /** 伺服器算好的狀態：`issued`／`expired`（到期不改列）／`redeemed`／`void`。 */
  state?: string
  /** 碼本身的到期（過期不改 status，前端看這一欄）。 */
  expires_at: string
  /** 兌換後權利的天數；null＝兌換後不到期。 */
  grant_term_days: number | null
  note: string | null
  issued_by: string | null
  issued_realm: 'staff' | 'portal' | string
  created_at: string | null
  redeemed_at: string | null
  redeemed_customer_id?: string | null
  redeemed_customer_name?: string | null
  /** `product`（在產品內輸入）／`portal`。 */
  redeemed_channel?: string | null
  voided_at: string | null
  voided_by: string | null
}
export interface IssuedGrantCode extends GrantCode {
  /** 明碼（`PG-XXXX-XXXX-XXXX-XXXX`）——**只此一次**，之後任何端點都讀不回來。 */
  code: string
}
export interface IssueGrantCodesIn {
  /** 碼的到期（ISO8601；1～365 天後）。 */
  expires_at: string
  grant_term_days: number | null
  note: string | null
  /** 一次產生幾組（1～50）。 */
  count: number
}

/** 批B #26：審核佇列裡的一筆待審版本（版本投影＋所屬 plugin 的摘要）。 */
export interface ReviewVersion extends MarketVersion {
  plugin: Pick<MarketPlugin, 'id' | 'key' | 'name' | 'product' | 'publisher_id' | 'publisher_name'>
  /** 已按過核准、正在等發布主機那一半完成（背景會接手）——這一列不能再核准或婉拒。 */
  approving: boolean
}
export interface ReviewQueue {
  /** `listing_status='pending'` 的第三方 plugin。 */
  listing_pending: MarketPlugin[]
  /** 公開的第三方 plugin 上傳、`review_status='pending'` 的版本。 */
  version_pending: ReviewVersion[]
}

export type PluginPatch = Partial<
  Pick<PluginIn, 'name' | 'summary' | 'description_md' | 'icon_data_uri' | 'homepage_url' | 'docs' | 'sort_order'>
> & { status?: PluginStatus }

const B = '/api/admin/plugin-market'

// --- 發行者 --------------------------------------------------------------------------------
export const listPublishers = () => api<{ items: Publisher[] }>(`${B}/publishers`)
/** 開通第三方發行者（批B #2，manager）。前綴重疊、蓋到既有 key、組織已是發行者 → 409。 */
export const createPublisher = (body: PublisherIn) =>
  api<Publisher>(`${B}/publishers`, { method: 'POST', body })
export const getPublisher = (id: string) => api<PublisherDetail>(`${B}/publishers/${id}`)
export const updatePublisher = (
  id: string,
  body: Partial<Pick<Publisher, 'display_name' | 'notes'>> & { status?: PluginStatus },
) => api<Publisher>(`${B}/publishers/${id}`, { method: 'PUT', body })
/** 登錄簽章公鑰（manager）。撞到既有指紋一律 409 `public_key_unavailable`（中性訊息）。 */
export const addPublisherKey = (id: string, publicKey: string, label?: string | null) =>
  api<PublisherKey>(`${B}/publishers/${id}/keys`, {
    method: 'POST',
    body: { public_key: publicKey, label: label || null },
  })
export const revokePublisherKey = (id: string, keyId: string, reason?: string | null) =>
  api<PublisherKey>(`${B}/publishers/${id}/keys/${keyId}/revoke`, {
    method: 'POST',
    body: { reason: reason || null },
  })

// --- plugin --------------------------------------------------------------------------------
export interface PluginFilter {
  publisherId?: string
  product?: string
  visibility?: string
  status?: string
  cursor?: string | null
  limit?: number
}
export const listPlugins = (f: PluginFilter = {}) =>
  api<Page<MarketPlugin>>(
    `${B}/plugins${qs({
      publisher_id: f.publisherId || undefined,
      product: f.product || undefined,
      visibility: f.visibility || undefined,
      status: f.status || undefined,
      cursor: f.cursor || undefined,
      limit: f.limit ? String(f.limit) : undefined,
    })}`,
  )
export const createPlugin = (body: PluginIn) => api<MarketPlugin>(`${B}/plugins`, { method: 'POST', body })
export const getPlugin = (id: string) => api<PluginDetail>(`${B}/plugins/${id}`)
export const updatePlugin = (id: string, body: PluginPatch) =>
  api<PluginDetail>(`${B}/plugins/${id}`, { method: 'PUT', body })

// --- 版本 ----------------------------------------------------------------------------------
/**
 * 上傳一個版本（原始 body）。dl 失敗 502 `dl_upload_failed`（`detail.version` 是那一版的投影）。
 *
 * ★ 版本說明**不放在網址上**（R3-1：中文說明一長，正式環境的 nginx 就回 414，說明全文還會進 access log）——
 *   上傳成功後再以 `updateVersionNotes`（`PUT …/versions/{id}`，JSON body）寫入。
 */
export const uploadVersion = (pluginId: string, file: Blob) =>
  apiUpload<MarketVersion>(`${B}/plugins/${pluginId}/versions`, file)
export const updateVersionNotes = (pluginId: string, versionId: string, releaseNotes: string) =>
  api<MarketVersion>(`${B}/plugins/${pluginId}/versions/${versionId}`, {
    method: 'PUT',
    body: { release_notes: releaseNotes },
  })
export const withdrawVersion = (pluginId: string, versionId: string, reason?: string | null) =>
  api<MarketVersion>(`${B}/plugins/${pluginId}/versions/${versionId}/withdraw`, {
    method: 'POST',
    body: { reason: reason || null },
  })

/**
 * 下載一個版本的包（批B #27，admin）：LS 代理串流待審或已發布的檔。
 *
 * ★ 走 fetch＋`Authorization` 標頭再轉 blob 存檔——**不可**把 token 放進 URL（會留在瀏覽器歷史、
 *   代理伺服器與 LS 的 access log 裡）。檔名取 `Content-Disposition`，取不到由呼叫端補。
 */
export async function downloadVersion(
  pluginId: string,
  versionId: string,
): Promise<{ blob: Blob; filename: string | null }> {
  const res = await api<Response>(`${B}/plugins/${pluginId}/versions/${versionId}/download`, { raw: true })
  return { blob: await res.blob(), filename: filenameFromDisposition(res.headers.get('Content-Disposition')) }
}
/**
 * 核准一個待審版本（批B #28，manager）：LS 把它從審核 channel 搬到主 channel 並發布。
 * 發布主機那一半失敗時 `dl_synced=false`（版本停在「核准處理中」，背景會接手）。
 */
export const approveVersion = (pluginId: string, versionId: string) =>
  api<MarketVersion & { dl_synced: boolean }>(`${B}/plugins/${pluginId}/versions/${versionId}/approve`, {
    method: 'POST',
  })
/** 婉拒一個待審版本（批B #29，manager）：理由必填；版號之後可以重傳。 */
export const rejectVersion = (pluginId: string, versionId: string, note: string) =>
  api<MarketVersion>(`${B}/plugins/${pluginId}/versions/${versionId}/reject`, {
    method: 'POST',
    body: { note },
  })

// --- 上架／下架（manager）--------------------------------------------------------------------
/**
 * 上架（批A #14）。批B 起第三方 plugin 必須是 `listing_status='pending'`（發行者已在 portal 申請），
 * 這一支同時就是「核准上架申請」；第一方照舊直接上架。
 */
export const publishPlugin = (id: string) =>
  api<PluginDetail & { dl_synced: boolean }>(`${B}/plugins/${id}/publish`, { method: 'POST' })
export const unpublishPlugin = (id: string) =>
  api<PluginDetail & { dl_synced: boolean }>(`${B}/plugins/${id}/unpublish`, { method: 'POST' })
/** 婉拒公開上架申請（批B #16，manager）：理由必填，plugin 維持私人。 */
export const rejectListing = (id: string, note: string) =>
  api<PluginDetail>(`${B}/plugins/${id}/listing/reject`, { method: 'POST', body: { note } })

// --- 審核佇列（批B #26）----------------------------------------------------------------------
export const listReviews = () => api<ReviewQueue>(`${B}/reviews`)

// --- 指派碼（批B #21～#23，admin）------------------------------------------------------------
export const listGrantCodes = (pluginId: string, f: { cursor?: string | null; limit?: number } = {}) =>
  api<Page<GrantCode>>(
    `${B}/plugins/${pluginId}/grant-codes${qs({
      cursor: f.cursor || undefined,
      limit: f.limit ? String(f.limit) : undefined,
    })}`,
  )
/** 發碼：回應裡的 `code` 是明碼，**只此一次**。 */
export const issueGrantCodes = (pluginId: string, body: IssueGrantCodesIn) =>
  api<{ items: IssuedGrantCode[] }>(`${B}/plugins/${pluginId}/grant-codes`, { method: 'POST', body })
export const voidGrantCode = (codeId: string) =>
  api<GrantCode>(`${B}/grant-codes/${codeId}/void`, { method: 'POST' })

// --- 權利 ----------------------------------------------------------------------------------
export interface GrantFilter {
  pluginId?: string
  customerId?: string
  status?: string
  cursor?: string | null
  limit?: number
}
export const listGrants = (f: GrantFilter = {}) =>
  api<Page<PluginGrant>>(
    `${B}/grants${qs({
      plugin_id: f.pluginId || undefined,
      customer_id: f.customerId || undefined,
      status: f.status || undefined,
      cursor: f.cursor || undefined,
      limit: f.limit ? String(f.limit) : undefined,
    })}`,
  )
/** 指派（upsert）：已有有效權利就就地更新期限與備註。`expires_at` null＝不到期。 */
export const upsertGrant = (body: {
  plugin_id: string
  customer_id: string
  expires_at: string | null
  note?: string | null
}) => api<PluginGrant>(`${B}/grants`, { method: 'POST', body })
export const updateGrant = (id: string, body: { expires_at?: string | null; note?: string | null }) =>
  api<PluginGrant>(`${B}/grants/${id}`, { method: 'PUT', body })
export const revokeGrant = (id: string, reason?: string | null) =>
  api<PluginGrant>(`${B}/grants/${id}/revoke`, { method: 'POST', body: { reason: reason || null } })

// --- 同步 ----------------------------------------------------------------------------------
export const getSync = () => api<SyncStatus>(`${B}/sync`)
export const runSync = () =>
  api<{ result: Record<string, unknown> | null; status: SyncStatus }>(`${B}/sync`, { method: 'POST' })
